import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Bot, Mic, MicOff, Send, Sparkles, CheckCircle2, User, Loader2, FileText } from "lucide-react";

const INITIAL_GREETING =
  "Hallo! Ich helfe Ihnen, schnell einen neuen PV-Lead zu erfassen. Erzählen Sie mir einfach, was Sie über den Kunden und das Projekt wissen – Sie können auch das Mikrofon nutzen.";

const FIELD_LABELS = {
  first_name: "Vorname", last_name: "Nachname", email: "E-Mail", phone: "Telefon",
  address: "Adresse", property_type: "Immobilientyp", roof_type: "Dachtyp",
  roof_area: "Dachfläche (m²)", roof_orientation: "Dachausrichtung",
  annual_consumption: "Stromverbrauch (kWh)", storage_wanted: "Speicher",
  wallbox_wanted: "Wallbox", timeframe: "Zeitraum",
};

export default function LeadNewAI() {
  const nav = useNavigate();
  const [messages, setMessages] = useState([
    { role: "assistant", content: INITIAL_GREETING },
  ]);
  const [data, setData] = useState({});
  const [missing, setMissing] = useState([]);
  const [complete, setComplete] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [listening, setListening] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const scrollRef = useRef(null);

  // Detect browser-native Web Speech API support
  const hasNativeSTT = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Setup Web Speech API (Chrome/Edge – fast, no server roundtrip)
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = "de-DE";
    rec.continuous = false;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let txt = "";
      for (let i = e.resultIndex; i < e.results.length; i++) txt += e.results[i][0].transcript;
      setInput(txt);
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setListening(false);
      if (e.error !== "no-speech" && e.error !== "aborted") {
        toast.error(`Spracheingabe-Fehler: ${e.error}`);
      }
    };
    recognitionRef.current = rec;
    return () => { try { rec.abort(); } catch { /* noop */ } };
  }, []);

  const toggleMicNative = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      rec.stop();
    } else {
      setInput("");
      try { rec.start(); setListening(true); }
      catch { /* already started */ }
    }
  };

  // MediaRecorder fallback (Safari / Firefox) → upload to /api/transcribe (Whisper)
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick the best supported MIME (Safari prefers mp4, Firefox webm, Chrome opus)
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mimeType = candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 200) {
          toast.error("Keine Sprache erkannt");
          setTranscribing(false);
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          const ext = (mr.mimeType || "audio/webm").includes("mp4") ? "mp4" : "webm";
          fd.append("audio", blob, `voice.${ext}`);
          const r = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (r.data?.text) setInput((prev) => prev ? `${prev} ${r.data.text}` : r.data.text);
          else toast.error("Konnte nichts erkennen");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Transkription fehlgeschlagen");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Mikrofon-Zugriff verweigert");
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
  };

  const toggleMic = () => {
    if (hasNativeSTT) {
      toggleMicNative();
    } else if (recording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const micActive = listening || recording;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/leads/ai-chat", { messages: newMessages, data });
      const { next_question, data: newData, missing: newMissing, complete: isComplete } = r.data;
      setData(newData || data);
      setMissing(newMissing || []);
      setComplete(!!isComplete);
      setMessages((m) => [...m, { role: "assistant", content: next_question || "Alles klar." }]);
    } catch (e) {
      toast.error("Fehler bei KI-Anfrage");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const finalize = async (alsoCreateOffer) => {
    setCreating(true);
    try {
      const r = await api.post("/leads/from-chat", { data, create_offer: alsoCreateOffer });
      toast.success(alsoCreateOffer && r.data.offer_id ? "Lead + Angebotsentwurf erstellt" : "Lead erfasst");
      if (alsoCreateOffer && r.data.offer_id) {
        nav(`/offers/${r.data.offer_id}/edit`);
      } else {
        nav(`/leads/${r.data.lead_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setCreating(false);
    }
  };

  const filledCount = Object.values(data).filter((v) => v !== "" && v != null).length;

  return (
    <div className="space-y-6" data-testid="lead-new-ai-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
      </button>

      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-500" />
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Lead per KI-Chat erfassen
            </h1>
          </div>
          <p className="mt-1 text-slate-600">Diktieren oder tippen Sie – die KI fragt fehlende Daten Schritt für Schritt ab.</p>
        </div>
        <Button data-testid="switch-to-form" variant="outline" onClick={() => nav("/leads/new")}>
          Lieber klassisches Formular
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat */}
        <Card className="lg:col-span-2 border-slate-200 flex flex-col h-[600px]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-history">
            {messages.map((m, i) => (
              <ChatBubble key={`${i}-${m.role}`} role={m.role} content={m.content} />
            ))}
            {sending && (
              <ChatBubble role="assistant" content={<span className="inline-flex items-center gap-2 text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> KI denkt nach …</span>} />
            )}
          </div>

          <div className="border-t border-slate-200 p-3">
            <div className="flex items-end gap-2">
              <Textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder={micActive ? "Höre zu …" : transcribing ? "Transkribiere …" : "Nachricht oder Mikrofon-Button nutzen…"}
                rows={2}
                className="flex-1 resize-none"
                disabled={sending}
              />
              <button
                data-testid="mic-btn"
                onClick={toggleMic}
                title={hasNativeSTT ? "Spracheingabe (Browser-nativ)" : "Spracheingabe (Whisper-Fallback)"}
                disabled={transcribing}
                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                  micActive ? "bg-red-600 text-white animate-pulse" : transcribing ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : micActive ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <Button data-testid="chat-send" onClick={send} disabled={sending || !input.trim()} className="bg-orange-600 hover:bg-orange-700 text-white h-10">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              {hasNativeSTT
                ? "Spracheingabe nutzt die Browser-API (Chrome/Edge, deutschsprachig)."
                : "Spracheingabe via OpenAI Whisper (für Safari/Firefox). Aufnahme stoppen → Transkription wird automatisch eingefügt."}
            </p>
          </div>
        </Card>

        {/* Side: collected data + actions */}
        <div className="space-y-4">
          <Card className="border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Erkannte Daten</h3>
              <Badge variant="outline" className="text-xs">{filledCount} erfasst</Badge>
            </div>
            <div className="space-y-2 text-sm max-h-[280px] overflow-y-auto">
              {Object.keys(FIELD_LABELS).map((k) => (
                <div key={k} className="flex items-start justify-between gap-2 py-1 border-b border-slate-100 last:border-0">
                  <span className="text-xs text-slate-500">{FIELD_LABELS[k]}</span>
                  <span className="text-xs font-medium text-slate-900 text-right max-w-[55%] truncate" title={data[k]}>
                    {data[k] !== "" && data[k] != null ? String(data[k]) : <span className="text-slate-300">—</span>}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {missing.length > 0 && !complete && (
            <Card className="border-amber-200 bg-amber-50 p-4 text-sm">
              <p className="font-semibold text-amber-900 mb-1">Noch offen:</p>
              <ul className="list-disc list-inside text-amber-900 text-xs space-y-0.5">
                {missing.map((m) => <li key={m}>{m}</li>)}
              </ul>
            </Card>
          )}

          {complete && (
            <Card className="border-emerald-200 bg-emerald-50 p-5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                <p className="font-semibold text-emerald-900">Alle Pflichtdaten erfasst!</p>
              </div>
              <p className="text-xs text-emerald-800 mb-4">Lead anlegen und direkt ein Angebot erstellen?</p>
              <div className="space-y-2">
                <Button
                  data-testid="finalize-with-offer"
                  onClick={() => finalize(true)}
                  disabled={creating}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                  Lead + Angebotsentwurf erstellen
                </Button>
                <Button
                  data-testid="finalize-only"
                  onClick={() => finalize(false)}
                  disabled={creating}
                  variant="outline"
                  className="w-full"
                >
                  Nur Lead speichern
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

const ChatBubble = ({ role, content }) => {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? "bg-slate-900 text-white" : "bg-orange-100 text-orange-700"
      }`}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
        isUser ? "bg-slate-900 text-white rounded-tr-sm" : "bg-slate-100 text-slate-900 rounded-tl-sm"
      }`}>
        {content}
      </div>
    </div>
  );
};
